import React, { useState, useEffect } from "react";
import { Folder, Plus, Trash2, Check } from "lucide-react";
import { INK, INK_2, INK_3, LINE, BURGUNDY, BAD_INK } from "../../Bureau/bureauTokens";
import { PALETTE } from "../atoms/internalEventColor";
import { httpClient } from "../../../HttpClient";
import { dialog } from "../../../components/ui/Dialog.js";

const fieldLabel = "text-[11px] font-medium";
const fieldInput = "h-9 px-3 text-xs outline-none";
const fieldStyle = { border: `1px solid ${LINE}`, borderRadius: 8, color: INK, background: "#fff" };

// Contenu (sans le chrome de modal) de la gestion des salles — extrait de
// RoomsManagerModal.jsx pour être réutilisé tel quel comme onglet "Salles"
// de PlanningSettingsModal.jsx, sans dupliquer ce CRUD.
//
// Sélecteur de cabinet : un founder/admin (allCabinetsAccess) n'a aucun
// userCabinets à déduire silencieusement (voir Planning.jsx), donc liste
// tous les cabinets via GET /api/cabinets — même convention que
// Admin/Create.js (fetch local, pas EtatGlobal) — présélectionné sur
// `defaultCabinetId` (le propre cabinet de l'utilisateur) quand il existe.
// Supprimer une salle efface aussi ses réservations à venir (cascade
// serveur) : confirmation via `dialog`.
function RoomsSettingsPanel({ rooms, defaultCabinetId, onChange }) {
  const [cabinets, setCabinets] = useState([]);
  const [cabinetId, setCabinetId] = useState(defaultCabinetId || "");
  const [nom, setNom] = useState("");
  const [capacite, setCapacite] = useState("6");
  const [emplacement, setEmplacement] = useState("");
  const [couleur, setCouleur] = useState(PALETTE[0]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    httpClient
      .get("/api/cabinets")
      .then(({ data }) => {
        setCabinets(data || []);
        setCabinetId((prev) => prev || defaultCabinetId || data?.[0]?.id || "");
      })
      .catch((err) => console.error("Erreur lors de la récupération des cabinets :", err));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const canSave = !!cabinetId && !!nom.trim() && !!capacite && !!emplacement.trim() && !saving;

  const addRoom = async () => {
    if (!canSave) return;
    setSaving(true);
    setError(null);
    try {
      await httpClient.post("/api/salles", {
        cabinetId,
        nom: nom.trim(),
        capacite: parseInt(capacite, 10),
        emplacement: emplacement.trim(),
        couleur,
      });
      setNom("");
      setEmplacement("");
      setCapacite("6");
      setCouleur(PALETTE[0]);
      await onChange();
    } catch (err) {
      setError(err.response?.data?.message || "Erreur lors de la création de la salle.");
    } finally {
      setSaving(false);
    }
  };

  const removeRoom = async (room) => {
    const ok = await dialog.confirm(`Supprimer "${room.nom}" ? Ses réservations à venir seront annulées.`, { variant: "danger" });
    if (!ok) return;
    await httpClient.delete(`/api/salles/${room.id}`);
    await onChange();
  };

  return (
    <>
      {error && (
        <p className="text-xs mb-2" style={{ color: BAD_INK }}>
          {error}
        </p>
      )}

      <div className="mb-3" style={{ background: "#FFF6F8", border: "1px solid #E8C8D2", borderRadius: 10, padding: "12px 14px" }}>
        <div className="flex items-center gap-1.5 text-xs font-semibold mb-2" style={{ color: BURGUNDY }}>
          <Plus size={13} /> Nouvelle salle
        </div>
        <label className={`block ${fieldLabel} mb-1`} style={{ color: INK_2 }}>
          Cabinet <span style={{ color: BAD_INK }}>*</span>
        </label>
        <select
          value={cabinetId}
          onChange={(e) => setCabinetId(Number(e.target.value))}
          className={`w-full ${fieldInput} mb-2.5 appearance-none cursor-pointer`}
          style={fieldStyle}
        >
          <option value="">— Choisir un cabinet —</option>
          {cabinets.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name} {c.city ? `(${c.city})` : ""}
            </option>
          ))}
        </select>
        <label className={`block ${fieldLabel} mb-1`} style={{ color: INK_2 }}>
          Nom <span style={{ color: BAD_INK }}>*</span>
        </label>
        <input
          value={nom}
          onChange={(e) => setNom(e.target.value)}
          placeholder="ex. Salle du conseil"
          className={`w-full ${fieldInput} mb-2.5`}
          style={fieldStyle}
        />
        <div className="grid grid-cols-2 gap-3 mb-2.5">
          <label className="flex flex-col gap-1">
            <span className={fieldLabel} style={{ color: INK_2 }}>
              Capacité <span style={{ color: BAD_INK }}>*</span>
            </span>
            <input type="number" min="1" value={capacite} onChange={(e) => setCapacite(e.target.value)} className={fieldInput} style={fieldStyle} />
          </label>
          <label className="flex flex-col gap-1">
            <span className={fieldLabel} style={{ color: INK_2 }}>
              Emplacement <span style={{ color: BAD_INK }}>*</span>
            </span>
            <input
              value={emplacement}
              onChange={(e) => setEmplacement(e.target.value)}
              placeholder="ex. 2e étage"
              className={fieldInput}
              style={fieldStyle}
            />
          </label>
        </div>
        <span className={fieldLabel} style={{ color: INK_2 }}>
          Couleur
        </span>
        <div className="flex items-center gap-2 mt-1.5">
          {PALETTE.map((c) => (
            <button
              key={c}
              onClick={() => setCouleur(c)}
              className="flex-shrink-0"
              style={{
                width: 24,
                height: 24,
                borderRadius: "50%",
                background: c,
                border: couleur === c ? `2px solid ${INK}` : "2px solid transparent",
                transform: couleur === c ? "scale(1.12)" : "scale(1)",
              }}
            />
          ))}
        </div>
        <button
          onClick={addRoom}
          disabled={!canSave}
          className="flex items-center gap-1.5 mt-3 px-4 py-2 text-xs font-semibold text-white disabled:opacity-50"
          style={{ background: BURGUNDY, borderRadius: 10 }}
        >
          <Check size={13} /> {saving ? "Ajout…" : "Ajouter la salle"}
        </button>
      </div>

      <div style={{ background: "#FAFAF8", border: `1px solid ${LINE}`, borderRadius: 10, padding: "12px 14px" }}>
        <div className="flex items-center gap-1.5 text-xs font-semibold mb-2" style={{ color: BURGUNDY }}>
          <Folder size={13} /> Salles existantes <span style={{ color: INK_3, fontWeight: 600 }}>({rooms.length})</span>
        </div>
        {rooms.length === 0 && (
          <p className="text-xs" style={{ color: INK_3 }}>
            Aucune salle pour le moment.
          </p>
        )}
        <div className="flex flex-col gap-1.5">
          {rooms.map((room) => (
            <div key={room.id} className="flex items-center gap-2 px-2.5 py-2" style={{ border: `1px solid ${LINE}`, borderRadius: 8, background: "#fff" }}>
              <span style={{ width: 10, height: 10, borderRadius: "50%", background: room.couleur, flexShrink: 0 }} />
              <span className="text-xs font-semibold flex-1 truncate" style={{ color: INK }}>
                {room.nom}
              </span>
              <span className="text-[10.5px]" style={{ color: INK_3 }}>
                {room.capacite} pl. · {room.emplacement}
              </span>
              <button
                onClick={() => removeRoom(room)}
                title="Supprimer"
                className="flex items-center justify-center flex-shrink-0"
                style={{ width: 26, height: 26, borderRadius: 6, color: BAD_INK }}
              >
                <Trash2 size={13} />
              </button>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

export default RoomsSettingsPanel;
